import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonButton,
    IonText,
    IonAlert,
    IonModal,
} from "@ionic/react";

// Question Interface
interface QuestionData {
    image: string;
    sentence: string;
    correctOption: string;
    options: string[];
}


const ThisThatTheseThose: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctOption) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                console.log(nextIndex)
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
                setShowImage(false);
                setSelectedOption(null);
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'ThisThatTheseThose.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/ThisThatTheseThose.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "This That These Those – LKG English Worksheet | Mama’s Guidance"; // Set page title

    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Help LKG students learn the correct use of "this," "that," "these," and "those" through simple explanations and interactive exercises at Mama’s Guidance. Perfect for early grammar development.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>This, That, These, Those</h1>

                                    <p>
                                        Teach your child to point, describe, and identify with this engaging <strong>demonstrative pronoun</strong> worksheet focused on <strong>This, That, These, and Those</strong>. Designed for LKG-level learners, this fun and visual activity helps children understand the difference between <strong>near and far</strong>, and <strong>singular and plural</strong>.
                                    </p>

                                    <h2>👉 What Are Demonstrative Pronouns?</h2>
                                    <p>
                                        Demonstrative pronouns are used to show or point to specific things or people:
                                    </p>
                                    <ul>
                                        <li><strong>This</strong> – one thing near</li>
                                        <li><strong>That</strong> – one thing far</li>
                                        <li><strong>These</strong> – many things near</li>
                                        <li><strong>Those</strong> – many things far</li>
                                    </ul>

                                    <h2>🧩 What’s in the Worksheet?</h2>
                                    <ul>
                                        <li>Match the picture with the correct word: this/that/these/those</li>
                                        <li>Circle the correct demonstrative pronoun</li>
                                        <li>Fill-in-the-blanks using this, that, these, or those</li>
                                        <li>Picture-based singular and plural practice</li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Understand and use demonstrative pronouns correctly</li>
                                        <li>Differentiate between near vs. far and one vs. many</li>
                                        <li>Build observation and grammar recognition skills</li>
                                        <li>Improve vocabulary and sentence formation</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show your child a picture or real object and ask: <strong>&apos;Is it near or far? One or many?&apos;</strong></li>
                                        <li>Guide them to choose the right word: this, that, these, or those.</li>
                                        <li>Use gestures like pointing to objects near and far to reinforce learning.</li>
                                        <li>Repeat with different examples until the child gains confidence.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/english-language/one-and-many"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>One and Many</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/english-language/rhyming-words"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Rhyming Words</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/english-language/opposites"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Opposites</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Helpful</h2>
                                    <p>
                                        Understanding <strong>this/that/these/those</strong> builds spatial awareness and sentence formation skills. It also prepares children for structured language use in everyday conversation.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>This, That, These, Those</h1>

                                <p>
                                    Teach your child to point, describe, and identify with this engaging <strong>demonstrative pronoun</strong> worksheet focused on <strong>This, That, These, and Those</strong>. Designed for LKG-level learners, this fun and visual activity helps children understand the difference between <strong>near and far</strong>, and <strong>singular and plural</strong>.
                                </p>

                                <h2>👉 What Are Demonstrative Pronouns?</h2>
                                <p>
                                    Demonstrative pronouns are used to show or point to specific things or people:
                                </p>
                                <ul>
                                    <li><strong>This</strong> – one thing near</li>
                                    <li><strong>That</strong> – one thing far</li>
                                    <li><strong>These</strong> – many things near</li>
                                    <li><strong>Those</strong> – many things far</li>
                                </ul>

                                <h2>🧩 What’s in the Worksheet?</h2>
                                <ul>
                                    <li>Match the picture with the correct word: this/that/these/those</li>
                                    <li>Circle the correct demonstrative pronoun</li>
                                    <li>Fill-in-the-blanks using this, that, these, or those</li>
                                    <li>Picture-based singular and plural practice</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Understand and use demonstrative pronouns correctly</li>
                                    <li>Differentiate between near vs. far and one vs. many</li>
                                    <li>Build observation and grammar recognition skills</li>
                                    <li>Improve vocabulary and sentence formation</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child a picture or real object and ask: <strong>&apos;Is it near or far? One or many?&apos;</strong></li>
                                    <li>Guide them to choose the right word: this, that, these, or those.</li>
                                    <li>Use gestures like pointing to objects near and far to reinforce learning.</li>
                                    <li>Repeat with different examples until the child gains confidence.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/english-language/opposites"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Opposites</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Helpful</h2>
                                <p>
                                    Understanding <strong>this/that/these/those</strong> builds spatial awareness and sentence formation skills. It also prepares children for structured language use in everyday conversation.
                                </p>
                            </article>
                            <h4>Fill in the blank with the correct option:</h4>

                            <p className="mt-5 clearfix">
                                {
                                    questions[currentIndex]?.correctOption === 'This' || questions[currentIndex]?.correctOption === 'These'
                                        ?
                                        <>
                                            <span className="float-start">
                                                <img className="img-thumbnail" width="20%" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} />
                                                <IonText style={{ marginLeft: '30px' }}>{questions[currentIndex]?.sentence}</IonText>
                                            </span>
                                        </>
                                        :
                                        <></>
                                }


                                {
                                    questions[currentIndex]?.correctOption === 'That' || questions[currentIndex]?.correctOption === 'Those'
                                        ?
                                        <>
                                            <IonText style={{ marginLeft: '30px' }}>{questions[currentIndex]?.sentence}</IonText>
                                            <span className="float-end">
                                                <img className="img-thumbnail" width="20%" src={process.env.REACT_APP_API_URL + questions[currentIndex]?.image} />
                                            </span>
                                        </>
                                        :
                                        <></>
                                }
                            </p>

                            <IonRadioGroup
                                value={selectedOption}
                                onIonChange={(e) => handleSubmit(e.detail.value)}
                            >
                                {questions[currentIndex]?.options.map((option, index) => (
                                    <IonItem
                                        className={` ${selectedOption === option
                                            ? option === questions[currentIndex]?.correctOption
                                                ? "correct"
                                                : "errorItems"
                                            : ""
                                            }`}
                                        key={index}>
                                        <IonLabel>{option}</IonLabel>
                                        <IonRadio slot="start" value={option} />
                                    </IonItem>
                                ))}
                            </IonRadioGroup>
                        </>

                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default ThisThatTheseThose;
